import { FrameTier, MilestoneKey, StreakMilestone } from './types';

/**
 * The launch ladder (EN-11154; 7 · 30 · 90 · 180 · 365 from 10/27). The server sends the live list in every
 * snapshot; this copy is only for storybook, tests and the dev mock.
 */
export const DEFAULT_MILESTONES: StreakMilestone[] = [
  {
    key: 'cowboy',
    name: 'Cowboy',
    days: 7,
    freezes: 1,
    frameKey: 'cowboy',
    avatarSetKey: 'cowboy'
  },
  {
    key: 'grinder',
    name: 'Grinder',
    days: 30,
    freezes: 1,
    frameKey: 'grinder',
    avatarSetKey: 'grinder'
  },
  {
    key: 'marshal',
    name: 'Marshal',
    days: 90,
    freezes: 1,
    frameKey: 'marshal',
    avatarSetKey: 'marshal'
  },
  {
    key: 'baron',
    name: 'Baron',
    days: 180,
    freezes: 3,
    frameKey: 'baron',
    avatarSetKey: 'baron'
  },
  {
    key: 'legend',
    name: 'Legend',
    days: 365,
    freezes: 3,
    frameKey: 'legend',
    avatarSetKey: 'legend'
  }
];

/**
 * Statuses that also grant a custom avatar request. Client-owned: the server's
 * milestone list has no such field (decided 10/26, EN-11151 STATUS.md).
 */
export const CUSTOM_AVATAR_MILESTONES: ReadonlySet<MilestoneKey> = new Set(['legend']);

/** Status avatar image URLs for a set key, from the manifest (useStatusAvatars). */
export type StatusAvatarLookup = (setKey: string | undefined) => string[];

/** A milestone with the rewards the client works out for it. */
export interface MilestoneRewards extends StreakMilestone {
  /** Stock status avatars in the manifest for its set; null until the manifest lists any. */
  avatars: number | null;
  /** Also grants a custom avatar request. */
  custom: boolean;
}

export function withRewards(
  milestones: StreakMilestone[],
  statusAvatars?: StatusAvatarLookup
): MilestoneRewards[] {
  return milestones.map((m) => {
    const count = statusAvatars?.(m.avatarSetKey).length ?? 0;
    return { ...m, avatars: count > 0 ? count : null, custom: CUSTOM_AVATAR_MILESTONES.has(m.key) };
  });
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "6 avatars", "1 avatar + custom"; without a manifest count, "avatars" or
 * "avatars + custom". `word` is the counted noun ("new avatar").
 */
export function avatarsLabel(m: MilestoneRewards, word = 'avatar'): string {
  const base = m.avatars === null ? `${word}s` : plural(m.avatars, word);
  return m.custom ? `${base} + custom` : base;
}

/** Avatar slots a status fills (stock plus the custom one), or null when unknown. */
export function avatarSlots(m: MilestoneRewards): number | null {
  return m.avatars === null ? null : m.avatars + (m.custom ? 1 : 0);
}

/**
 * Flame colour stage: 0 unlit, 1 red (before the first status), then one
 * stage per status. Positional, so a sixth milestone gets the last colour.
 */
export type FlameStage = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export function sortedMilestones<M extends StreakMilestone>(milestones: M[]): M[] {
  return milestones.slice().sort((a, b) => a.days - b.days);
}

export function currentMilestone<M extends StreakMilestone>(
  milestones: M[],
  days: number
): M | undefined {
  let found: M | undefined;
  for (const m of sortedMilestones(milestones)) {
    if (m.days <= days) found = m;
  }
  return found;
}

export function nextMilestone<M extends StreakMilestone>(
  milestones: M[],
  days: number
): M | undefined {
  return sortedMilestones(milestones).find((m) => m.days > days);
}

export function flameStageFor(milestones: StreakMilestone[], days: number): FlameStage {
  if (days < 1) return 0;
  const reached = sortedMilestones(milestones).filter((m) => m.days <= days).length;
  return Math.min(6, 1 + reached) as FlameStage;
}

export function frameTierFor(milestones: StreakMilestone[], days: number): FrameTier | null {
  const cur = currentMilestone(milestones, days);
  return cur ? cur.frameKey : null;
}

/**
 * How far the statuses rail's fill reaches, in rungs from the first rung's
 * center (EN-11162): 0 before the first status, the rung just passed plus the
 * way to the next, the last rung once every status is reached.
 */
export function ladderFillSteps(milestones: StreakMilestone[], days: number): number {
  const list = sortedMilestones(milestones);
  const n = list.length;
  if (!n || days < list[0].days) return 0;
  if (days >= list[n - 1].days) return n - 1;
  for (let i = 0; i < n - 1; i++) {
    const a = list[i];
    const b = list[i + 1];
    if (days >= a.days && days < b.days) return i + (days - a.days) / (b.days - a.days);
  }
  return 0;
}
